import { useEffect, useMemo } from "react";
import { useThree } from "@react-three/fiber";
import {
  CylinderGeometry,
  InstancedBufferGeometry,
  InstancedBufferAttribute,
  Object3D,
} from "three";
import { GCodeMaterial } from "./gcode-material";
import { GCodePrintData } from "./gcode-print";
import { Triple } from "_/types";

/**
 * Component for managing the instanced mesh representing an extrusion from a single toolhead.
 */
interface GCodeMeshProps {
  printData: GCodePrintData;
  color: number;
  topLayer?: number;
  bottomLayer?: number;
  pathStart?: number;
  pathEnd?: number;
  featureTypes?: number[];
  position?: Triple<number>;
  onAfterRender?: Object3D["onAfterRender"];
}

const ALL_FEATURE_TYPES = Array.from({ length: 16 }, () => 1);
const DEFAULT_POSITION: Triple<number> = [0, 0, 0];

/**
 * Component for rendering an extrusion instanced mesh, generating geometry attributes and managing material uniform updates.
 */
export const GCodeMesh = ({
  printData,
  color,
  topLayer = printData.topLayer,
  bottomLayer = 0,
  pathStart = 0,
  pathEnd = 100,
  featureTypes = ALL_FEATURE_TYPES,
  position = DEFAULT_POSITION,
  onAfterRender,
}: GCodeMeshProps) => {
  const { invalidate } = useThree();

  const material = useMemo(() => new GCodeMaterial({ color }), [color]);

  const instancedGeometry = useMemo(() => {
    const cylinderGeometry = new CylinderGeometry(0.275, 0.275, 1, 4);
    cylinderGeometry.scale(1, 1, 0.5);

    const ig = new InstancedBufferGeometry();
    ig.index = cylinderGeometry.index;
    ig.attributes.position = cylinderGeometry.attributes.position;
    ig.attributes.uv = cylinderGeometry.attributes.uv;
    ig.attributes.normal = cylinderGeometry.attributes.normal;

    ig.setAttribute(
      "layer",
      new InstancedBufferAttribute(printData.segmentLayer, 1)
    );
    ig.setAttribute(
      "featureType",
      new InstancedBufferAttribute(printData.segmentFeatureType, 1)
    );
    ig.setAttribute(
      "startVertex",
      new InstancedBufferAttribute(printData.segmentStartPoints, 3)
    );
    ig.setAttribute(
      "endVertex",
      new InstancedBufferAttribute(printData.segmentEndPoints, 3)
    );

    ig.instanceCount = printData.totalSegments;

    return ig;
  }, [printData]);

  useEffect(() => {
    return () => {
      instancedGeometry.dispose();
      material.dispose();
    };
  }, [instancedGeometry, material]);

  useEffect(() => {
    function clampLayerSelection(layer: number) {
      return Math.min(Math.max(layer, 0), printData.topLayer);
    }

    material.uniforms.topLayer.value = clampLayerSelection(topLayer);
    material.uniforms.bottomLayer.value = clampLayerSelection(bottomLayer);
    material.uniforms.featureTypes.value = featureTypes;

    material.needsUpdate = true;
    invalidate();
  }, [
    invalidate,
    pathStart,
    pathEnd,
    topLayer,
    bottomLayer,
    featureTypes,
    material,
    printData.topLayer,
  ]);

  return (
    <mesh
      geometry={instancedGeometry}
      material={material}
      position={position}
      onAfterRender={onAfterRender}
      frustumCulled={false}
    />
  );
};
